import React, { useState } from 'react';
import { PlayIcon, CloseIcon, CopyIcon, CheckIcon } from './Icons';
import VintageRadioPlayer, { pauseOtherRecordings } from './VintageRadioPlayer';

// Copies the recording's audio URL to the clipboard, with a brief
// checkmark swap as feedback instead of a toast.
function CopyRecordingButton({ url }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard access denied/unavailable — nothing to fall back to
    }
  };

  return (
    <button
      type="button"
      className="recording-copy-btn"
      onClick={handleCopy}
      aria-label="Copy recording link"
      title={copied ? 'Copied!' : 'Copy recording link'}
    >
      {copied ? <CheckIcon width={11} height={11} /> : <CopyIcon width={11} height={11} />}
    </button>
  );
}

// Starts as a small "Listen" button and only mounts the actual <audio>
// element once clicked, so a page listing many calls doesn't eagerly request
// every recording — just the ones someone actually plays.
//
// variant="radio" (Call History modal) swaps the native player for the
// vintage radio, which opens paused: the agent presses its Play button.
// downloadName (no extension) names the radio's downloaded file.
function RecordingPlayer({ url, variant, downloadName }) {
  const [expanded, setExpanded] = useState(false);

  if (!url) return <>-</>;

  if (!expanded) {
    return (
      <span className="recording-player-collapsed">
        <button type="button" className="recording-play-btn" onClick={() => setExpanded(true)}>
          <PlayIcon width={11} height={11} />
          Listen
        </button>
        <CopyRecordingButton url={url} />
      </span>
    );
  }

  if (variant === 'radio') {
    return (
      <div className="recording-radio-wrap">
        <VintageRadioPlayer url={url} downloadName={downloadName} />
        <span className="recording-radio-tools">
          <CopyRecordingButton url={url} />
          <button
            type="button"
            className="recording-collapse-btn"
            onClick={() => setExpanded(false)}
            aria-label="Close player"
            title="Close player"
          >
            <CloseIcon width={12} height={12} />
          </button>
        </span>
      </div>
    );
  }

  return (
    <span className="recording-player">
      <audio
        controls
        autoPlay
        src={url}
        className="recording-audio"
        data-recording-audio=""
        onPlay={(event) => pauseOtherRecordings(event.currentTarget)}
      />
      <CopyRecordingButton url={url} />
      <button
        type="button"
        className="recording-collapse-btn"
        onClick={() => setExpanded(false)}
        aria-label="Collapse player"
        title="Collapse player"
      >
        <CloseIcon width={12} height={12} />
      </button>
    </span>
  );
}

export default RecordingPlayer;
